import { trackEvent } from '@/analytics'
import { EVENT_ACTION, EVENT_CATEGORY, EVENT_NAME } from '@/constants/analytics'
import { GOAL_ROW } from '@/constants/goal'
import { IDEA_LIST_ROW } from '@/constants/ideaList'
import { INITIATIVE_ROW, TASK_ROW } from '@/constants/initiative'
import { KR_ROW } from '@/constants/kr'
import * as keymapService from '@/services/keymapService/keymapService'
import getParentVueComponent from '@/utils/getParentVueComponent'
import {
  getGoalPageRouteConfig,
  getInitiativePageRouteConfig,
  getKrPageRouteConfig
} from '@/utils/navigationConfigs'
import scrollToElement from '@/utils/scrollToElement'
import InitiativesMixin from './InitiativesMixin'

export default {
  mixins: [InitiativesMixin],
  methods: {
    selectedGoalViewer() {
      return this.getVueComponent('GoalViewer')
    },
    getVueComponent(componentName, childNode) {
      if (!childNode) {
        childNode = this.selectedElement()
      }
      return getParentVueComponent(childNode, componentName)
    },
    selectedKrRow() {
      return this.getVueComponent('kr-row')
    },
    isKrSelected() {
      const selectedElem = this.selectedElement()
      return selectedElem && selectedElem.classList.contains('kr')
    },
    getParentKr(node) {
      if (!node) return null
      else if (node.classList.contains('kr')) return node
      else return this.getParentKr(node.parentElement)
    },
    isChildGoalSelected() {
      const selectedElem = this.selectedElement()
      return selectedElem && selectedElem.classList.contains('child-goal')
    },
    isInitiativeSelected() {
      const selectedElem = this.selectedElement()
      return selectedElem && selectedElem.classList.contains('initiative-row')
    },
    isTaskSelected() {
      const selectedElem = this.selectedElement()
      return selectedElem && selectedElem.classList.contains('task-row')
    },
    isIdeaListSelected() {
      const selectedElem = this.selectedElement()
      return selectedElem && selectedElem.classList.contains('idea-list-row')
    },
    selectedElement() {
      return keymapService.selectedListItem(this.$refs.listContainer || this.$el)
    },
    selectPreviousItem(event) {
      event.preventDefault() // prevent default scrolling of page on up array key
      this.selectedGoalViewer() &&
        this.selectedGoalViewer().$el.classList.remove('self-or-child-selected')
      keymapService.selectListItem(this.$refs.listContainer, 'previous')
      this.selectedGoalViewer() &&
        this.selectedGoalViewer().$el.classList.add('self-or-child-selected')
      let selectedElement = this.selectedElement()
      if (selectedElement?.classList.contains('kr')) {
        selectedElement = selectedElement.querySelector('.kr-wrapper')
      }
      selectedElement && scrollToElement(selectedElement)
      this.focusTabPin()
    },
    selectNextItem(event) {
      event.preventDefault() // prevent default scrolling of page on down array key
      this.selectedGoalViewer() &&
        this.selectedGoalViewer().$el.classList.remove('self-or-child-selected')
      keymapService.selectListItem(this.$refs.listContainer, 'next')
      this.selectedGoalViewer() &&
        this.selectedGoalViewer().$el.classList.add('self-or-child-selected')
      let selectedElement = this.selectedElement()
      if (selectedElement.classList.contains('kr')) {
        selectedElement = selectedElement.querySelector('.kr-wrapper')
      }
      selectedElement && scrollToElement(selectedElement)
      this.focusTabPin()
    },
    getKRContainer(elem) {
      if (elem.classList.contains('checkin-btn-wrapper')) return elem
      else if (elem) return this.getKRContainer(elem.parentElement)
      else return null
    },
    handleSpacebar(event) {
      const selectedElem = this.selectedElement()
      const isKrNode = this.isKrSelected()
      if (isKrNode) {
        event.preventDefault() // prevent scroll
        const checkinBtn = selectedElem.querySelector('.checkin-btn')
        if (checkinBtn) {
          const krContainer = this.getKRContainer(checkinBtn)
          krContainer.classList.add('kr-show-checkin-button')
          setTimeout(checkinBtn.click.bind(checkinBtn), 0)
          // Adding setTimeout, because space (keyStroke event) is propagating to selected textbox in checkin popover and emptying field
        }
      } else if (this.isInitiativeSelected() || this.isTaskSelected()) {
        event.preventDefault() // prevent scroll
        this.doneUndone()
      }
    },
    doneUndone(event) {
      const selectedElem = this.selectedElement()
      const initiativeVue = selectedElem.__vue__
      if (
        initiativeVue &&
        (!initiativeVue.$el.contains(document.activeElement) ||
          document.activeElement.classList.contains('tab-pin'))
      ) {
        const initiative = initiativeVue.initiative
        initiativeVue.changeIsDoneAndClosed(!initiative.isDoneAndClosed)
        event.preventDefault()
      }
    },
    viewItem(event) {
      event.preventDefault()
      const selectedElem = this.selectedElement()
      const isKrNode = this.isKrSelected()
      if (isKrNode) {
        const krId = selectedElem.dataset.krId
        const location = getKrPageRouteConfig({
          krId,
          orgId: this.orgId
        })
        this.$router.push(location)
      } else if (this.isChildGoalSelected()) {
        const location = getGoalPageRouteConfig({
          orgId: this.orgId,
          goalId: selectedElem.dataset.goalId
        })
        this.$router.push(location)
      } else if (this.isInitiativeSelected() || this.isTaskSelected()) {
        const selectedVueComponent =
          this.getInitiativeRowVue(selectedElem, TASK_ROW) ||
          this.getInitiativeRowVue(selectedElem, INITIATIVE_ROW)
        const location = getInitiativePageRouteConfig({
          orgId: selectedVueComponent.initiative.organizationId,
          initiativeId: selectedVueComponent.initiative.id
        })
        this.$router.push(location)
      }
    },
    addTask() {
      const selectedElem = this.selectedElement()
      const initiative = this.getVueComponent(INITIATIVE_ROW, selectedElem)
      initiative.$refs.tasks.create()
    },
    addInitiative() {
      const selectedElem = this.selectedElement()
      if (selectedElem) {
        if (this.isTaskSelected()) {
          // add task
          this.addTask()
        } else {
          // add initiative
          const kr = this.getParentKr(selectedElem)
          if (kr) {
            kr.__vue__.addInitiative()
          }
        }
      }
    },
    triggerDeleteInitiative() {
      const selectedElem = this.selectedElement()
      if (selectedElem && (this.isTaskSelected() || this.isInitiativeSelected())) {
        const vueComponent = this.getVueComponent(TASK_ROW) || this.getVueComponent(INITIATIVE_ROW)
        vueComponent.triggerDeleteInitiative()
      }
    },
    cancelGoalKrInlineEditing() {
      const active = document.activeElement
      const component =
        this.getVueComponent(KR_ROW, active) || this.getVueComponent(GOAL_ROW, active)
      component && component.cancelInlineEditing()
    },
    cancelEditing() {
      const active = document.activeElement
      if (active) {
        const initiative = this.getVueComponent(INITIATIVE_ROW, active)
        const ideaList = this.getVueComponent(IDEA_LIST_ROW, active)
        const goal = this.getVueComponent(GOAL_ROW, active)
        const kr = this.getVueComponent(KR_ROW, active)
        if (initiative && active.dataset.isTitleField) {
          this.cancelInitiativeEditing()
        } else if (ideaList) {
          ideaList.cancelInlineEditing()
        } else if (goal || kr) {
          this.cancelGoalKrInlineEditing()
        }
      }
    },
    editItem(event) {
      const selectedElement = this.selectedElement()
      if (selectedElement) {
        if (this.isTaskSelected()) {
          const vueComponent = this.getVueComponent(TASK_ROW)
          vueComponent.editInitiative(event)
          return
        }
        const selectedComponent =
          this.getVueComponent(INITIATIVE_ROW) ||
          this.getVueComponent('kr-row') ||
          this.getVueComponent('GoalViewer')
        if (selectedComponent) {
          setTimeout(() => {
            selectedComponent.editable = true
          }, 0)
        }
      }
    },
    trackCommentAnalytics(selectedComponent) {
      let eventName = EVENT_NAME.GOAL
      if (selectedComponent.$options.name === 'kr-row') {
        eventName = EVENT_NAME.KR
      } else if (selectedComponent.$options.name === INITIATIVE_ROW) {
        eventName = EVENT_NAME.INITIATIVE
      } else if (selectedComponent.$options.name === TASK_ROW) {
        eventName = EVENT_NAME.TASK
      }
      trackEvent(EVENT_CATEGORY.NAVIGATION, EVENT_ACTION.ACTIVITY_MODAL_KB, eventName)
    },
    comment() {
      const selectedElement = this.selectedElement()
      if (selectedElement) {
        const selectedComponent =
          this.getVueComponent(TASK_ROW) ||
          this.getVueComponent(INITIATIVE_ROW) ||
          this.getVueComponent('kr-row') ||
          this.getVueComponent('GoalViewer')
        if (selectedComponent) {
          setTimeout(() => {
            selectedComponent.showActivityModal({ focusAddCommentOnLoad: true })
            this.trackCommentAnalytics(selectedComponent)
          }, 0)
        }
      }
    }
  }
}
